<template>
  <main class="sale-units">
    <section class="section directory-hero">
      <div class="shell hero-layout">
        <header class="page-head compact">
          <h1>Units for sale</h1>
          <p>
            Equipment we maintain, have worked on, or that belongs to people we know. A
            customer mentions something they are ready to part with, or something they are
            looking for — this page is where the two meet. We tell you what we know about the
            machine; the sale itself is theirs to make.
          </p>
        </header>

        <router-link class="explain-button" :to="{ path: '/services-request', query: { intent: 'sell' } }">
          <span>Have something you want listed here?</span>
          <span class="explain-cta">Talk to us</span>
          <span class="explain-arrow" aria-hidden="true">→</span>
        </router-link>
      </div>
    </section>

    <section class="section directory-section">
      <div class="shell">
        <div class="result-heading">
          <div>
            <h2>Currently available</h2>
            <!-- Only when there is something to select. Telling a visitor to pick a machine
                 directly above "nothing is listed at the moment" reads as a broken page. -->
            <p v-if="liveTiles.length">
              Select a machine to see hours, condition, photographs, and its maintenance
              record.
            </p>
          </div>
        </div>

        <ListingTiles v-if="liveTiles.length" :tiles="liveTiles" aria-label="Centerfire sale units" />

        <!-- Three states, and they are not the same statement. A quiet week is true about the
             business; a server we could not reach is a fault, and saying "nothing is listed"
             then tells a visitor a machine does not exist when it does. -->
        <p v-else-if="loadError" class="empty-directory">
          {{ loadError }} You can also call <a :href="phoneHref">{{ brand.phone }}</a> and we
          will tell you what is available.
        </p>

        <p v-else-if="!loading" class="empty-directory">
          Nothing is listed at the moment. Machines come up regularly — call
          <a :href="phoneHref">{{ brand.phone }}</a> and tell us what you are looking for.
        </p>

        <button v-if="next" class="btn" :disabled="loading" @click="loadMore">Load more listings</button>
        <div class="directory-footnote">
          <!-- "May not be the seller", never "are not": some machines here are customers'
               and some are our own shop equipment, and anonymity is the SELLER'S choice,
               not a blanket rule (Daniel 2026-08-30). A listing names the machine and
               nothing else by default; a seller who wants to be named can be. -->
          <p>
            Seller contact information is kept private for Centerfire Services use only.
            Contact Centerfire with questions about a listing.
          </p>
          <p>
            Centerfire is a maintenance and repair company — not a dealer, and not a sales
            company. Some machines here belong to customers selling through us and some are
            our own shop equipment; unless a listing says otherwise, we are not the seller
            and we do not verify title or ownership. Most off-road equipment changes hands
            on a bill of sale alone — satisfy yourself as to both before money moves.
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import ListingTiles from '@/components/ListingTiles.vue';
import { brand } from '@/data/content';
import { loadSaleUnitTilesResult } from '@/data/machinesForSale';

export default {
  name: 'MachinesForSaleView',
  components: { ListingTiles },
  data() {
    return {
      brand,
      tiles: [],
      next: null,
      loading: true,
      loadError: '',
    };
  },
  computed: {
    phoneHref() {
      return `tel:${this.brand.phone.replace(/\D/g, '')}`;
    },
    // The endpoint already returns live listings first and sold ones after, newest first
    // within each. Split rather than re-sort: sorting again here would let the page and the
    // record disagree about an ordering rule that was asked for by name.
    liveTiles() {
      return this.tiles.filter((tile) => tile.status !== 'Sold');
    },
    soldTiles() {
      return this.tiles.filter((tile) => tile.status === 'Sold');
    },
  },
  methods: {
    async loadMore() {
      this.loading = true;
      const {tiles,error,next} = await loadSaleUnitTilesResult(this.next);
      this.tiles.push(...tiles); this.loadError=error || ''; if(!error) this.next=next;
      this.loading=false;
    },
  },
  async created() {
    const { tiles, error, next } = await loadSaleUnitTilesResult();
    this.next = next;
    this.tiles = tiles;
    this.loadError = error || '';
    this.loading = false;
  },
};
</script>

<style scoped>
.sale-units {
  min-height: 100vh;
  background:
    radial-gradient(circle at 82% 9%, rgba(255, 90, 2, 0.13), transparent 27%),
    var(--black);
}

.directory-hero {
  padding-top: clamp(10rem, 15vw, 12rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.hero-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(270px, 0.65fr);
  gap: clamp(2rem, 7vw, 6rem);
  align-items: end;
}

.compact {
  margin: 0;
}

.compact h1 {
  max-width: 12ch;
  font-size: clamp(3rem, 7vw, 6.3rem);
  line-height: 0.94;
  text-transform: uppercase;
}

.compact p {
  margin-top: 1rem;
  font-size: clamp(1rem, 1.5vw, 1.18rem);
}

.explain-button {
  width: min(100%, 290px);
  aspect-ratio: 1;
  justify-self: end;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: clamp(1.3rem, 3vw, 2rem);
  border: 2px solid var(--orange);
  background: rgba(255, 90, 2, 0.06);
  color: var(--white);
  font-family: 'Jost', 'Barlow', sans-serif;
  /* A full sentence, so it sets smaller than the maintained-units button's three words.
     Same box, same weight - the type just has more to carry. */
  font-size: clamp(1.35rem, 2.3vw, 1.95rem);
  font-weight: 400;
  line-height: 1.08;
  text-transform: uppercase;
  transition: background-color 0.35s ease, color 0.35s ease, transform 0.35s ease;
}

.explain-button:hover,
.explain-button:focus-visible {
  background: var(--orange);
  color: var(--black);
  transform: translateY(-3px);
}

.explain-cta {
  align-self: stretch;
  padding: 0.7rem 1rem;
  background: var(--orange);
  color: var(--black);
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
}

.explain-button:hover .explain-cta,
.explain-button:focus-visible .explain-cta {
  background: var(--black);
  color: var(--orange);
}

.explain-arrow {
  align-self: end;
  font-size: 2rem;
  line-height: 1;
}

.directory-section {
  padding-top: clamp(3rem, 6vw, 5rem);
}

/* Separated from the live grid above it, so the boundary between "you can buy this" and
   "this is gone" is visible without reading a heading. */
.sold-heading {
  margin-top: clamp(3rem, 6vw, 4.5rem);
  padding-top: clamp(1.5rem, 3vw, 2rem);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.result-heading {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  align-items: end;
  margin-bottom: 1.5rem;
}

.result-heading h2 {
  margin-bottom: 0.35rem;
  font-size: var(--h2);
  font-weight: 400;
}

.result-heading > div > p {
  color: rgba(255, 255, 255, 0.76);
}

.empty-directory {
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: var(--radius);
  color: rgba(255, 255, 255, 0.78);
}

.empty-directory a {
  color: var(--orange);
}

/* Same fine print the maintained-units directory carries, on purpose. */
.directory-footnote {
  max-width: 760px;
  margin-top: 1.4rem;
  padding-left: 1rem;
  border-left: 2px solid rgba(255, 90, 2, 0.7);
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.9rem;
}

.directory-footnote p + p {
  margin-top: 0.7rem;
}

@media (max-width: 820px) {
  .hero-layout {
    grid-template-columns: 1fr;
  }

  .explain-button {
    width: min(100%, 260px);
    justify-self: start;
  }

  .result-heading {
    display: grid;
    align-items: start;
  }
}
</style>
